/**
 * Turn a picked colour into a hue the renderers can multiply.
 *
 * Every material lays its colour over the greyscale engraving tint as a
 * multiply, so the colour has to carry hue only — brightness stays the Engraving
 * Tint slider's job. Normalising by the brightest channel does exactly that: the
 * hue is exactly what was picked, and a multiply can never brighten past 1.0
 * anyway, so clamping instead would flatten the very ratio that gives a colour
 * its identity (it is what turned gold into lemon yellow).
 *
 * Shared by the 2D label preview and the 3D shader so both agree.
 */

export const DEFAULT_MATERIAL_COLOR = '#c69e4a'

/**
 * Coerce typed input to '#rrggbb', or null while it is still half-written.
 * Accepts a missing '#' and the 3-digit CSS shorthand people paste.
 */
export const normalizeHexInput = (value: string): string | null => {
	const trimmed = (value || '').trim()
	const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
	const expanded = /^#[0-9a-fA-F]{3}$/.test(withHash)
		? `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`
		: withHash
	return /^#[0-9a-fA-F]{6}$/.test(expanded) ? expanded.toLowerCase() : null
}

/** Parse '#rrggbb' (or 'rrggbb') to 0-255 components; null if unparseable. */
export const parseHexColor = (hex: string | null | undefined): [number, number, number] | null => {
	if (!hex) return null
	const value = hex.trim().replace(/^#/, '')
	if (!/^[0-9a-fA-F]{6}$/.test(value)) return null
	return [
		parseInt(value.slice(0, 2), 16),
		parseInt(value.slice(2, 4), 16),
		parseInt(value.slice(4, 6), 16),
	]
}

/**
 * Hue of a picked colour as 0-1 factors, normalised so the brightest channel is
 * 1.0. Pure black normalises to black rather than white — engraving in black is
 * a legitimate choice, and there is no hue to recover from it.
 */
export const normalizeMaterialColor = (hex: string | null | undefined): [number, number, number] | null => {
	const rgb = parseHexColor(hex)
	if (!rgb) return null

	const max = Math.max(rgb[0], rgb[1], rgb[2])
	if (max === 0) return [0, 0, 0]

	return [rgb[0] / max, rgb[1] / max, rgb[2] / max]
}

/** Packed 0xRRGGBB of the normalised hue, for THREE.Color. */
export const materialColorToHex = (hex: string | null | undefined): number | null => {
	const hue = normalizeMaterialColor(hex)
	if (!hue) return null
	const to255 = (c: number) => Math.round(c * 255)
	return (to255(hue[0]) << 16) | (to255(hue[1]) << 8) | to255(hue[2])
}

/**
 * CSS colour for the 2D preview: the picked hue at the engraving tint's
 * brightness, matching what the 3D multiply produces.
 */
export const materialColorToCss = (
	hex: string | null | undefined,
	engravingTint: number
): string | null => {
	const hue = normalizeMaterialColor(hex)
	if (!hue) return null
	const brightness = Math.max(0, Math.min(1, engravingTint)) * 255
	const channel = (c: number) => Math.round(c * brightness)
	return `rgb(${channel(hue[0])}, ${channel(hue[1])}, ${channel(hue[2])})`
}
